import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    CircularProgress,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction } from "react";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetSubAdmins, useDeleteSubAdmin, useUpdateSubAdmin } from "../../api/hooks/useSubAdmin";
import { showError, showSuccess } from "../../helpers/toast";
import type { SubAdmin } from "../../api/types/subAdmin.types";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import Pagination from "../../components/Panigation";

interface SubadminListProps {
    searchTerm: string;
    page: number;
    limit: number;
    onPageChange: (newPage: number) => void;
    setPage: Dispatch<SetStateAction<number>>;
}

const SubadminList: React.FC<SubadminListProps> = ({ searchTerm, page, limit, onPageChange, setPage }) => {
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [subAdminToDelete, setSubAdminToDelete] = useState<string | null>(null);

    // Fetch sub-admins
    const { data: subAdminsData, isLoading: isLoadingSubAdmins, refetch: refetchSubAdmins, error: subAdminsError } = useGetSubAdmins(
        {
            ...(searchTerm.trim() && { search: searchTerm.trim() }),
            page,
            limit,
            offset: (page - 1) * limit,
        },
        { enabled: true }
    );

    // Extract sub-admins and totalCount from response
    const { subAdmins, totalCount } = useMemo(() => {
        const data = subAdminsData?.data;
        let subAdminsList: SubAdmin[] = [];
        let total = 0;

        if (Array.isArray(data)) {
            subAdminsList = data;
            total = data.length;
        } else if (data && typeof data === 'object') {
            const dataObj = data as any;
            if ('list' in dataObj && Array.isArray(dataObj.list)) {
                subAdminsList = dataObj.list;
                total = dataObj.totalCount || dataObj.list.length;
            } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
                subAdminsList = dataObj.data;
                total = dataObj.totalCount || dataObj.data.length;
            } else if (dataObj.totalCount !== undefined) {
                total = dataObj.totalCount;
            }
        }

        if (!Array.isArray(subAdminsList)) {
            subAdminsList = [];
        }

        return {
            subAdmins: subAdminsList,
            totalCount: total || subAdminsList.length,
        };
    }, [subAdminsData]);

    // Delete sub-admin mutation
    const { mutate: deleteSubAdmin, isPending: isDeleting } = useDeleteSubAdmin({
        onSuccess: (response) => {
            showSuccess(response.message || "Sub-admin deleted successfully");
            refetchSubAdmins();
            setDeleteDialogOpen(false);
            setSubAdminToDelete(null);
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to delete sub-admin");
        },
    });

    // Update sub-admin status mutation
    const { mutate: updateSubAdminStatus, isPending: isUpdatingStatus } = useUpdateSubAdmin({
        onSuccess: (response) => {
            showSuccess(response.message || "Status updated successfully");
            refetchSubAdmins();
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update status");
        },
    });

    const handleStatusChange = (subAdminId: string, currentStatus: boolean) => {
        updateSubAdminStatus({
            id: subAdminId,
            data: { isBlocked: !currentStatus },
        });
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const handleDeleteSubAdmin = (subAdminId: string) => {
        setSubAdminToDelete(subAdminId);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        if (subAdminToDelete) {
            deleteSubAdmin(subAdminToDelete);
        }
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setSubAdminToDelete(null);
    };

    const handleViewSubAdmin = (subAdmin: SubAdmin) => {
        navigate("/manage-subadmin/detail", { state: { subAdminId: subAdmin._id } });
    };

    const handleEditSubAdmin = (subAdmin: SubAdmin) => {
        navigate("/manage-subadmin/add", { state: { subAdminId: subAdmin._id } });
    };

    if (isLoadingSubAdmins) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (subAdminsError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <p>Error loading sub-admins. Please try again.</p>
            </Box>
        );
    }

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <>
            <TableContainer className="table_container">
                {subAdmins.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                        <p>No sub-admins found.</p>
                    </Box>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow hover>
                                <TableCell>Sr. No.</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone Number</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Created Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subAdmins.map((subAdmin: SubAdmin, index: number) => (
                                <TableRow hover key={subAdmin._id}>
                                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                    <TableCell>
                                        <a
                                            href="/manage-subadmin/detail"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleViewSubAdmin(subAdmin);
                                            }}
                                            className="user_block click_action"
                                        >
                                            <Avatar
                                                src={subAdmin.image || subAdmin.profile || "/static/images/user_placeholder.png"}
                                                alt={subAdmin.fullName}
                                                sx={{ width: 40, height: 40, mr: 1 }}
                                            />
                                            <p><b>{subAdmin.fullName}</b></p>
                                        </a>
                                    </TableCell>
                                    <TableCell>{subAdmin.email}</TableCell>
                                    <TableCell>
                                        {subAdmin.countryCode || ""} {subAdmin.phone}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            // Check permissionDetails first
                                            if (subAdmin.permissionDetails?.name) {
                                                return subAdmin.permissionDetails.name;
                                            }
                                            // Check permissionId - can be object or string
                                            const permissionId = subAdmin.permissionId as any;
                                            if (permissionId && typeof permissionId === 'object' && permissionId.name) {
                                                return permissionId.name;
                                            }
                                            if (typeof permissionId === 'string') {
                                                return permissionId;
                                            }
                                            // Check if permission is an array
                                            if (Array.isArray(subAdmin.permission) && subAdmin.permission.length > 0) {
                                                const firstPermission = subAdmin.permission[0] as any;
                                                if (firstPermission && firstPermission.name) {
                                                    return firstPermission.name;
                                                }
                                            }
                                            // Check if permission is an object with name
                                            if (subAdmin.permission && typeof subAdmin.permission === 'object' && !Array.isArray(subAdmin.permission) && 'name' in subAdmin.permission) {
                                                return (subAdmin.permission as any).name;
                                            }
                                            // Check if permission is a string
                                            if (typeof subAdmin.permission === 'string') {
                                                return subAdmin.permission;
                                            }
                                            return "N/A";
                                        })()}
                                    </TableCell>
                                    <TableCell>{formatDate(subAdmin.createdAt)}</TableCell>
                                    <TableCell>
                                        <SwitchToggle
                                            checked={!subAdmin.isBlocked}
                                            onChange={() => handleStatusChange(subAdmin._id, subAdmin.isBlocked || false)}
                                            disabled={isUpdatingStatus || isDeleting}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <div className="table_actions v2">
                                            <IconButton
                                                onClick={() => handleViewSubAdmin(subAdmin)}
                                                disabled={isDeleting}
                                                title="View Sub-Admin"
                                            >
                                                <img src="/static/images/eye_icon.svg" alt="View" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleEditSubAdmin(subAdmin)}
                                                disabled={isDeleting}
                                                title="Edit Sub-Admin"
                                            >
                                                <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDeleteSubAdmin(subAdmin._id)}
                                                disabled={isDeleting}
                                                title="Delete Sub-Admin"
                                            >
                                                <img src="/static/images/trash2_icon.svg" alt="Delete" />
                                            </IconButton>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            {/* Pagination */}
            {!isLoadingSubAdmins && !subAdminsError && totalCount > 0 && (
                <Pagination
                    module={subAdmins}
                    page={page}
                    onPageChange={onPageChange}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    setPage={setPage}
                    limit={limit}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialogOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete Sub-Admin"
                message="Are you sure you want to delete this sub-admin? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonColor="error"
                isLoading={isDeleting}
                loadingText="Deleting..."
            />
        </>
    );
};

export default SubadminList;
